'use client';

import { IconX } from '@tabler/icons-react';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useAnnouncement } from './useAnnouncementBarMessage';
import { twMerge } from 'tailwind-merge';
import AnnouncementBarPopup from './AnnouncementBarPopup';
import { IAnnouncementBar } from '@/interfaces/homepage';

const ANNOUNCEMENT_BAR_HEIGHT_VAR = '--announcement-bar-height';

interface Props {
  isLoading: boolean;
  content: IAnnouncementBar | null;
  position?: 'fixed' | 'static'; // Fixed is used for top of the page. static is used for announcementbar 2 which can be any where as a section
}
export function AnnouncementBar({ isLoading, content, position = 'fixed' }: Readonly<Props>) {
  const pathname = usePathname();
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const [popupState, setPopupState] = useState<{ open: boolean; itemId: string; announcementBarId: string }>({
    open: false,
    itemId: '',
    announcementBarId: '',
  });

  const { announcement } = useAnnouncement(content, popupState.open);
  const barRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const measureParagraphRef = useRef<HTMLParagraphElement>(null);

  const isHomeOrPreviewPage = pathname === '/' || pathname === '/preview';
  const shouldRender = isVisible && isHomeOrPreviewPage && (isLoading || Boolean(announcement?.Text));

  function handleClick(announcementBarId?: string, itemId?: string) {
    if (announcementBarId && itemId) {
      setPopupState({ itemId, announcementBarId, open: true });
    }
  }
  // Check if collapsed message needs "Show more"
  useEffect(() => {
    if (!buttonRef.current || typeof ResizeObserver === 'undefined') return;

    const el = buttonRef.current;

    const checkOverflow = () => {
      // Force a single-line layout to measure. Inline styles are used instead of a
      // `truncate` class because the expanded state applies `whitespace-normal`,
      // which sits in the same utilities layer and would win the class fight.
      const prevWhiteSpace = el.style.whiteSpace;
      const prevOverflow = el.style.overflow;
      el.style.whiteSpace = 'nowrap';
      el.style.overflow = 'hidden';

      const hasOverflow = el.scrollWidth > el.clientWidth;

      el.style.whiteSpace = prevWhiteSpace;
      el.style.overflow = prevOverflow;

      setShowToggle(hasOverflow);
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [announcement?.Text, isExpanded]);

  // Publish the bar's height. Only the fixed bar owns this variable: the header and
  // page layout offset themselves by it. The static bar sits in normal flow.
  useEffect(() => {
    if (position !== 'fixed') return;

    const root = document.documentElement;

    if (!shouldRender || !barRef.current || typeof ResizeObserver === 'undefined') {
      root.style.setProperty(ANNOUNCEMENT_BAR_HEIGHT_VAR, '0px');
      return;
    }

    const el = barRef.current;
    const updateHeight = () => root.style.setProperty(ANNOUNCEMENT_BAR_HEIGHT_VAR, `${el.offsetHeight}px`);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.setProperty(ANNOUNCEMENT_BAR_HEIGHT_VAR, '0px');
    };
  }, [position, shouldRender, isLoading, announcement?.Text, isExpanded]);

  if (!shouldRender) return null;

  return (
    <>
      <div
        ref={barRef}
        style={{
          color: position === 'fixed' ? 'black' : content?.TextColor || 'black',
          backgroundColor: position === 'fixed' ? '#e7e5e4' : content?.BackgroundColor || '#e7e5e4',
        }}
        className={twMerge(position === 'fixed' ? 'sticky top-0 z-[999]' : 'relative !-mb-12 !-mt-0 max-xs:!-mb-8')}
        data-testid={`announcement-bar-${position}`}
      >
        <div className="relative flex flex-col justify-center px-11">
          {isLoading ? (
            <p className="loading h-[39px] animate-spin" aria-hidden="true" data-testid={`announcement-bar-${position}-loading`} />
          ) : (
            <>
              {/* Hidden measurement element "NEEDED" */}
              <p
                ref={measureParagraphRef}
                className="pointer-events-none invisible absolute left-11 right-11 p-3.5 text-center text-base font-medium text-black"
                aria-hidden="true"
              >
                {announcement?.Text}
              </p>

              {/* Visible message */}
              <button
                type="button"
                ref={buttonRef}
                onClick={() => handleClick(content?.AnnouncementBarId, announcement?.ItemId)}
                aria-label={announcement?.ItemId ? 'Open announcement popup' : undefined}
                className={twMerge(
                  'block h-fit w-full bg-transparent p-3.5 text-center text-base font-medium active:scale-100',
                  showToggle ? 'pb-0 pt-1' : '',
                  isExpanded ? 'whitespace-normal break-words' : 'truncate',
                  announcement?.ItemId ? 'cursor-pointer underline' : 'cursor-default',
                )}
              >
                {announcement?.Text}
              </button>
            </>
          )}

          {showToggle && (
            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              data-testid={`announcement-bar${position}-show-${isExpanded ? 'less' : 'more'}-button`}
              aria-label={`announcement-bar-${position}show-${isExpanded ? 'less' : 'more'}-button`}
              className="h-fit bg-transparent p-0 text-base font-bold underline"
            >
              Show {isExpanded ? 'less' : 'more'}
            </button>
          )}
        </div>

        {content?.HasCloseButton && (
          <button
            type="button"
            className="absolute right-3 top-1/2 h-fit -translate-y-1/2 bg-transparent p-0"
            onClick={() => setIsVisible(false)}
            data-testid={`announcement-bar-${position}-cancel-button`}
            aria-label={`announcement-bar-${position}-cancel-button`}
          >
            <IconX className="h-9 w-9 p-1" />
          </button>
        )}
      </div>

      <AnnouncementBarPopup
        itemId={popupState.itemId}
        announcementBarId={popupState.announcementBarId}
        open={popupState.open}
        closePopup={() => {
          setPopupState({ itemId: '', announcementBarId: '', open: false });
        }}
      />
    </>
  );
}
